import { useMemo } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { PaperPlaneTilt, Calendar, Clock, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Compact reminder bar for entity detail pages.
 * Shows: fecha_inicio, fecha_termino, days remaining, responsable email,
 * and a "Send reminder now" button hitting the corresponding /notify endpoint.
 */
export default function ReminderBar({
  entityKind,           // "commitments" | "projects" | "rca"
  entityId,
  form,
  setForm,
  startKey,             // "fecha_inicio"
  endKey,               // "fecha_compromiso" | "fecha_termino" | "fecha_comprometida"
  emailKey = "responsable_email",
  onSaveNeeded,         // async () => void  — called when email/dates change and need to be persisted
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = form?.[endKey] ? new Date(form[endKey]) : null;
  const daysLeft = useMemo(() => {
    if (!due) return null;
    const diff = Math.ceil((due.getTime() - today.getTime()) / 86400000);
    return diff;
  }, [form?.[endKey]]); // eslint-disable-line react-hooks/exhaustive-deps

  const tone = daysLeft == null ? "slate"
    : daysLeft < 0 ? "red"
    : daysLeft <= 3 ? "red"
    : daysLeft <= 7 ? "amber"
    : daysLeft <= 30 ? "blue"
    : "emerald";

  const badgeStyles = {
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
    emerald: "bg-brand-green-50 text-brand-green-700 border-brand-green-200",
  }[tone];

  const daysLabel = daysLeft == null ? "Sin fecha de término"
    : daysLeft < 0 ? `Atrasado ${Math.abs(daysLeft)} día${Math.abs(daysLeft) === 1 ? "" : "s"}`
    : daysLeft === 0 ? "Vence hoy"
    : `Faltan ${daysLeft} día${daysLeft === 1 ? "" : "s"}`;

  const sendNow = async () => {
    if (!form?.[endKey]) {
      toast.error("Define una fecha de término antes de enviar el recordatorio.");
      return;
    }
    if (!form?.[emailKey]) {
      toast.error("Define el email del responsable antes de enviar el recordatorio.");
      return;
    }
    // Persist any unsaved changes first; if it fails, abort the notification
    if (onSaveNeeded) {
      try {
        await onSaveNeeded();
      } catch {
        toast.error("No se pudo guardar los cambios antes de enviar el recordatorio. Revisa los campos.");
        return;
      }
    }
    try {
      const { data } = await api.post(`/${entityKind}/${entityId}/notify`);
      toast.success(`Recordatorio enviado a ${data.to} (${data.days_left} días restantes)`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ")
        : "No se pudo enviar el recordatorio";
      toast.error(msg);
    }
  };

  return (
    <Card className="rounded-md border-slate-200">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[150px]">
            <Label className="text-xs flex items-center gap-1 text-slate-600">
              <Calendar size={12} /> Fecha de inicio
            </Label>
            <Input
              type="date"
              value={form?.[startKey] || ""}
              onChange={(e) => setForm({ ...form, [startKey]: e.target.value || null })}
              data-testid="reminder-start-date"
              className="h-9"
            />
          </div>
          <div className="min-w-[150px]">
            <Label className="text-xs flex items-center gap-1 text-slate-600">
              <Calendar size={12} /> Fecha de término
            </Label>
            <Input
              type="date"
              value={form?.[endKey] || ""}
              onChange={(e) => setForm({ ...form, [endKey]: e.target.value || null })}
              data-testid="reminder-end-date"
              className="h-9"
            />
          </div>
          <div className="min-w-[100px]">
            <Label className="text-xs flex items-center gap-1 text-slate-600">
              <Clock size={12} /> Vencimiento
            </Label>
            <div
              className={`h-9 inline-flex items-center px-3 rounded-md border text-sm font-medium ${badgeStyles}`}
              data-testid="reminder-days-left"
            >
              {daysLabel}
            </div>
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs flex items-center gap-1 text-slate-600">
              <EnvelopeSimple size={12} /> Email(s) responsable (separa con coma para varios)
            </Label>
            <Input
              type="text"
              value={form?.[emailKey] || ""}
              onChange={(e) => setForm({ ...form, [emailKey]: e.target.value })}
              placeholder="nombre@empresa.cl, otro@empresa.cl"
              data-testid="reminder-email"
              className="h-9"
            />
          </div>
          <div>
            <Button
              type="button"
              onClick={sendNow}
              className="h-9 bg-brand-green-700 hover:bg-brand-green-800 text-white"
              data-testid="reminder-send-now-btn"
            >
              <PaperPlaneTilt size={14} className="mr-1.5" /> Enviar recordatorio ahora
            </Button>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          El sistema envía recordatorios automáticos 30 y 3 días antes del vencimiento. Este botón fuerza el envío con la cantidad de días que falten ahora mismo.
        </div>
      </CardContent>
    </Card>
  );
}
